import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Modal, Select, Spin, Alert, Tag, Button, Progress } from "antd";
import { CheckCircleFilled, StarFilled, ArrowRightOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import axiosInstance from "api/axiosInstance";
import API from "api/endpoints";
import { JobApplicationStatusEnum } from "Constants/Enums/JobApplicantStatusEnum";
import { LabelMeta, LoadingState, EmptyState, AIUnavailableBanner, theme } from "../components/ui";

// =====================================================================
// CareerPathTab — internal applicants only, post-FeedbackAdded.
//
// Per scope agreed with backend, the page shows only 3 sections:
//   1. Performance Standing   (hidden if no review on file)
//   2. Current Trajectory     (display only — no actions)
//   3. Target Trajectory      (single path, fit % badge, green-check on the
//                              role the applicant applied for, action button
//                              labelled "Transition to {JobTitle}")
//
// Data: ONE call — GET /api/CareerPath/job-applicants/{id}/career-transition-analytics
// Performance review comes from the same response — no extra API call.
//
// The green-check on the target role is driven by isTargetJobTitle: true
// from backend. While that flag isn't fully seeded yet, a name-match
// fallback against the job posting's title keeps the UI functional.
// =====================================================================
function CareerPathTab({ jobApplicationId, fallbackTargetTitle, applicantStatus }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [transitionOpen, setTransitionOpen] = useState(false);
  // Once a transition succeeds in-session, render the success state directly
  // without hitting analytics again (backend 500s on hired applicants).
  const [justTransitioned, setJustTransitioned] = useState(false);

  // Per Mudassir (2026-05-20): the analytics endpoint throws 500 for hired
  // applicants ("Employee is already hired for this application"). Skip the
  // fetch entirely once status === Hired and let the empty-state branch
  // render the "Transition complete" success card.
  const isHired = applicantStatus === JobApplicationStatusEnum.Hired || justTransitioned;

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(
        API.careerPaths.getApplicantTransitionAnalytics(jobApplicationId)
      );
      setData(res.data);
    } catch (err) {
      console.error("Error loading career transition analytics:", err);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [jobApplicationId]);

  useEffect(() => {
    if (!jobApplicationId) return;
    if (isHired) {
      setLoading(false);
      setData(null);
      return;
    }
    fetchAnalytics();
  }, [jobApplicationId, fetchAnalytics, isHired]);

  if (loading) return <LoadingState label="Loading career analytics..." />;
  if (!data) {
    if (isHired) {
      return (
        <EmptyState
          icon="✅"
          title="Transition complete"
          hint="This applicant has been successfully transitioned and hired. No further action needed on the career path."
        />
      );
    }
    return (
      <EmptyState
        icon="🎯"
        title="Career analytics not available"
        hint="Once the applicant's performance and career data are seeded, transition analytics will appear here."
      />
    );
  }

  const employee = data.employee;
  const review = data.performanceReview;
  const currentPath = data.currentPath;
  const fitScore = data.applicationAiEvaluation?.fitScore;

  // AI-unavailable detection (per Mudassir May-21): when AI service is
  // down the backend returns 200 with null AI fields. Show banner so HR
  // knows the missing fit % / target path isn't a data problem.
  const aiUnavailable =
    data.applicationAiEvaluation != null && fitScore == null;

  // Per Mudassir's API response (2026-05-20): the target path is returned
  // as `targetCareerPath` (not `targetPath`). Keep `targetPath` as a fallback
  // for any older snapshots. Same logic for alternative paths.
  const apiTargetPath = data.targetCareerPath ?? data.targetPath ?? null;
  const apiAlternativePaths =
    data.alternativeCareerPaths ?? data.alternativePaths ?? [];

  // Find the path that actually contains the target job title (the one
  // applicant applied for). Previously we fell back to currentPath which
  // caused the Target section to render the same data as Current — instead
  // pick the path whose jobTitles contain isTargetJobTitle (or the name
  // fallback). If none match, prefer the API's explicit target path, else null.
  const candidatePaths = [
    apiTargetPath,
    ...apiAlternativePaths,
    data.currentPath,
  ].filter(Boolean);
  const targetPath =
    findTargetPath(candidatePaths, fallbackTargetTitle) || apiTargetPath;

  const targetJobTitle = findTargetJobTitle(
    [targetPath, currentPath, ...apiAlternativePaths].filter(Boolean),
    fallbackTargetTitle
  );

  return (
    <div style={{ padding: "1.5rem" }}>
      {aiUnavailable && (
        <AIUnavailableBanner message="The AI-generated fit score and target trajectory are not available right now. Performance data and current trajectory below are still accurate." />
      )}
      {/* 1 · Performance Standing — only when a review exists */}
      <PerformanceStanding review={review} />

      {/* 2 · Current Trajectory — display only */}
      <SectionHeader icon="🧭">Current Trajectory</SectionHeader>
      <TrajectoryRoadmap path={currentPath} variant="current" />

      {/* 3 · Target Trajectory — fit % badge + Transition action */}
      <SectionHeader icon="🚀">Target Trajectory</SectionHeader>
      <TrajectoryRoadmap
        path={targetPath}
        variant="target"
        fitScore={fitScore}
        targetJobTitle={targetJobTitle}
        onStartTransition={() => setTransitionOpen(true)}
      />

      <StartTransitionModal
        open={transitionOpen}
        onClose={() => setTransitionOpen(false)}
        onSuccess={() => {
          // Backend analytics endpoint 500s once the applicant is hired,
          // so flip a local flag instead of re-fetching — the empty-state
          // branch will render the "Transition complete" success card.
          setTransitionOpen(false);
          setData(null);
          setJustTransitioned(true);
        }}
        // Per backend (2026-05-20 testing): the applicant's actual employee id
        // lives at data.internalApplication.employeeId. The top-level
        // data.employee.employeeId is a different value (looks like supervisor
        // info on this response), so don't use it for the supervisors lookup.
        employeeId={
          data.internalApplication?.employeeId ??
          employee?.id ??
          employee?.employeeId
        }
        careerPathId={targetPath?.id}
        jobTitle={targetJobTitle}
        applicationId={data.internalApplication?.jobApplicantId ?? jobApplicationId}
      />
    </div>
  );
}

CareerPathTab.propTypes = {
  jobApplicationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  fallbackTargetTitle: PropTypes.string,
  applicantStatus: PropTypes.number,
};

// =====================================================================
// Performance Standing — required per the doc (1 of 3 sections).
// Shows an empty state when no review exists, so the section is always
// visible and the page structure is consistent.
// =====================================================================
function PerformanceStanding({ review }) {
  if (!review) {
    return (
      <div style={{ marginBottom: "1.5rem" }}>
        <SectionHeader icon="⭐">Performance Standing</SectionHeader>
        <div
          style={{
            background: theme.surface,
            borderRadius: 20,
            boxShadow: theme.shadowCard,
            border: `1px solid ${theme.border}`,
            padding: "18px 22px",
            color: theme.textMuted,
            fontSize: "0.9rem",
          }}
        >
          No performance review on file for this employee yet.
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: "1.5rem" }}>
      <SectionHeader icon="⭐">Performance Standing</SectionHeader>
      <div
        className="perf-standing-grid"
        style={{
          background: theme.surface,
          borderRadius: 20,
          boxShadow: theme.shadowCard,
          border: `1px solid ${theme.border}`,
          padding: "16px 22px",
          display: "grid",
          gridTemplateColumns: "minmax(170px, 0.7fr) 2fr",
          gap: 20,
          alignItems: "center",
        }}
      >
        <div>
          <div style={{ fontSize: "0.72rem", color: theme.textMuted, fontWeight: 600, letterSpacing: 0.4 }}>
            OVERALL RATING
          </div>
          <div style={{ fontSize: "1.05rem", fontWeight: 700, color: theme.accentDeep, marginTop: 2 }}>
            {review.performanceRatingCode?.name || "Not rated"}
          </div>
          {review.totalPerformanceScore != null && (
            <div
              style={{
                background: theme.greenBg,
                color: theme.accentDeep,
                padding: "4px 12px",
                borderRadius: 40,
                fontSize: "0.76rem",
                fontWeight: 700,
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                marginTop: 8,
              }}
            >
              <StarFilled />
              {review.totalPerformanceScore}% score
            </div>
          )}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(90px, 1fr))", gap: 16 }}>
          <MetricBar
            label="Accountability"
            value={review.weightedAccountabilityScore ?? 0}
            denom={50}
            stroke="#1890ff"
          />
          <MetricBar
            label="Performance Factors"
            value={review.weightedPerformanceFactorsScore ?? 0}
            denom={40}
            stroke={theme.accent}
          />
          <MetricBar
            label="Org. Behaviors"
            value={review.weightedDynamicOrganizationScore ?? 0}
            denom={10}
            stroke="#22c55e"
          />
        </div>
      </div>
    </div>
  );
}
PerformanceStanding.propTypes = { review: PropTypes.object };

function MetricBar({ label, value, denom, stroke }) {
  const pct = denom ? Math.min(100, (value / denom) * 100) : 0;
  return (
    <div>
      <div
        style={{
          fontSize: "0.7rem",
          color: theme.textMuted,
          fontWeight: 600,
          marginBottom: 4,
          textTransform: "uppercase",
          letterSpacing: 0.4,
        }}
      >
        {label}
      </div>
      <Progress percent={pct} strokeColor={stroke} size="small" format={() => `${value}/${denom}`} />
    </div>
  );
}
MetricBar.propTypes = {
  label: PropTypes.string,
  value: PropTypes.number,
  denom: PropTypes.number,
  stroke: PropTypes.string,
};

// =====================================================================
// Trajectory Roadmap — used for both Current + Target paths.
// Target variant shows the fit % badge + Transition action.
// =====================================================================
function TrajectoryRoadmap({ path, variant, fitScore, targetJobTitle, onStartTransition }) {
  if (!path) {
    return (
      <EmptyState
        icon={variant === "current" ? "🧭" : "🚀"}
        title={variant === "current" ? "No current trajectory" : "No target trajectory"}
        hint="Career path data isn't seeded for this employee yet."
      />
    );
  }
  const jobTitles = (path.jobTitles || []).sort(
    (a, b) => (a.stepOrder || 0) - (b.stepOrder || 0)
  );

  const isTarget = variant === "target";

  return (
    <div
      style={{
        background: theme.surface,
        borderRadius: 20,
        boxShadow: theme.shadowCard,
        border: `1px solid ${theme.border}`,
        borderLeft: `5px solid ${isTarget ? theme.accent : "#1890ff"}`,
        padding: "16px 20px",
        marginBottom: "1.5rem",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <div style={{ fontSize: "1rem", fontWeight: 700, color: theme.accentDeep }}>
            {path.name}
          </div>
          {path.publicId && (
            <Tag color="gold" style={{ fontSize: 10, margin: 0 }}>
              {path.publicId}
            </Tag>
          )}
          {isTarget && fitScore != null && (
            <span
              style={{
                background: theme.greenBg,
                color: theme.accentDeep,
                padding: "3px 12px",
                borderRadius: 40,
                fontSize: "0.74rem",
                fontWeight: 700,
              }}
            >
              {Math.round(fitScore)}% fit
            </span>
          )}
        </div>

        {isTarget && targetJobTitle && (
          <Button
            type="primary"
            icon={<ArrowRightOutlined />}
            onClick={onStartTransition}
            style={{
              background: theme.accentDeep,
              borderColor: theme.accentDeep,
              borderRadius: 40,
              fontWeight: 600,
              paddingLeft: 18,
              paddingRight: 18,
            }}
          >
            Transition to {targetJobTitle.name}
          </Button>
        )}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 10,
        }}
      >
        {jobTitles.map((job) => (
          <JobTitleNode key={job.id} job={job} variant={variant} />
        ))}
      </div>
    </div>
  );
}
TrajectoryRoadmap.propTypes = {
  path: PropTypes.object,
  variant: PropTypes.oneOf(["current", "target"]).isRequired,
  fitScore: PropTypes.number,
  targetJobTitle: PropTypes.object,
  onStartTransition: PropTypes.func,
};

function JobTitleNode({ job, variant }) {
  const isTarget = job.isTargetJobTitle === true;
  // In the target trajectory, suppress the blue "Current" badge — the
  // focus there should be on the target role only.
  const showCurrent = job.isCurrent === true && variant === "current" && !isTarget;
  const borderColor = isTarget ? theme.accent : showCurrent ? "#1890ff" : theme.border;

  return (
    <div
      style={{
        background: "#fafdfb",
        border: `1.5px solid ${borderColor}`,
        borderRadius: 14,
        padding: "10px 12px",
        position: "relative",
      }}
    >
      {isTarget && (
        <div style={badgeStyle(theme.accent)}>
          <CheckCircleFilled />
          {variant === "target" ? "This Role" : "Target"}
        </div>
      )}
      {showCurrent && <div style={badgeStyle("#1890ff")}>Current</div>}

      <div style={{ fontSize: "0.92rem", fontWeight: 700, color: theme.accentDeep, marginBottom: 2 }}>
        {job.name}
      </div>
      {job.publicId && (
        <Tag style={{ fontSize: 9, marginTop: 2, marginBottom: 4 }}>{job.publicId}</Tag>
      )}
      {formatExperience(job) && (
        <div style={{ fontSize: "0.72rem", color: theme.textMuted }}>
          {formatExperience(job)}
        </div>
      )}
      {job.skillMatchPercentage != null && job.skillMatchPercentage > 0 && (
        <div style={{ fontSize: "0.7rem", color: theme.accentDeep, fontWeight: 600, marginTop: 4 }}>
          {Math.round(job.skillMatchPercentage)}% skill match
        </div>
      )}
    </div>
  );
}
JobTitleNode.propTypes = {
  job: PropTypes.object.isRequired,
  variant: PropTypes.string,
};

// =====================================================================
// Start Transition Modal — supervisor selection only.
// =====================================================================
function StartTransitionModal({ open, onClose, onSuccess, employeeId, careerPathId, jobTitle, applicationId }) {
  const [supervisors, setSupervisors] = useState([]);
  const [loadingSupervisors, setLoadingSupervisors] = useState(false);
  const [selectedSupervisor, setSelectedSupervisor] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchSupervisors = useCallback(async () => {
    if (!employeeId || !jobTitle?.id) return;
    try {
      setLoadingSupervisors(true);
      // Per Mudassir 2026-05-20: pass isInternal=true so the supervisors API
      // applies the internal-application flow validation (allows same-path
      // transitions etc.).
      const res = await axiosInstance.get(
        API.careerPaths.getSupervisors(employeeId, jobTitle.id, true)
      );
      setSupervisors(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Error loading supervisors:", err);
      setSupervisors([]);
    } finally {
      setLoadingSupervisors(false);
    }
  }, [employeeId, jobTitle?.id]);

  useEffect(() => {
    if (open) {
      setSelectedSupervisor(null);
      fetchSupervisors();
    }
  }, [open, fetchSupervisors]);

  const handleSubmit = async () => {
    if (!selectedSupervisor) {
      toast.warning("Please select a supervisor.");
      return;
    }
    try {
      setSubmitting(true);
      // Per Mudassir 2026-05-20: separate endpoint for internal-applicant
      // transitions. Payload now includes applicationId (from the analytics
      // response's internalApplication object) and drops the
      // isForInternalApplication flag since the URL implies the flow.
      await axiosInstance.post(API.careerPaths.submitInternalTransition, {
        employeeId: Number(employeeId),
        careerPathId: Number(careerPathId),
        jobTitleId: Number(jobTitle.id),
        supervisorId: Number(selectedSupervisor),
        applicationId: Number(applicationId),
      });
      toast.success("Career transition started successfully.");
      onSuccess?.();
    } catch (err) {
      console.error("Failed to create transition:", err);
      toast.error(
        err.response?.data?.details ||
          err.response?.data?.message ||
          "Failed to create transition. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={
        <div>
          <div style={{ fontSize: "0.72rem", color: theme.textMuted, fontWeight: 600, letterSpacing: 0.4 }}>
            START CAREER TRANSITION
          </div>
          <div style={{ fontSize: "1.1rem", fontWeight: 700, color: theme.accentDeep }}>
            {jobTitle?.name || "Selected role"}
          </div>
        </div>
      }
      open={open}
      onCancel={onClose}
      okText="Confirm Transition"
      onOk={handleSubmit}
      okButtonProps={{
        loading: submitting,
        disabled: !selectedSupervisor || loadingSupervisors,
        style: { background: theme.accentDeep, borderColor: theme.accentDeep },
      }}
      destroyOnHidden
    >
      <Alert
        type="info"
        showIcon
        message="The applicant has been matched to this role."
        description="Assign a new supervisor to start their transition. The role itself is fixed based on the job posting they applied for."
        style={{ borderRadius: 12, marginBottom: 16 }}
      />

      <div style={{ marginBottom: 16 }}>
        <LabelMeta>Target Role</LabelMeta>
        <div
          style={{
            background: theme.greenBg,
            border: `1px solid ${theme.accent}`,
            borderRadius: 12,
            padding: "8px 14px",
            marginTop: 6,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <CheckCircleFilled style={{ color: theme.accent }} />
          <span style={{ fontWeight: 600, color: theme.accentDeep }}>
            {jobTitle?.name || "Selected role"}
          </span>
          {jobTitle?.publicId && (
            <Tag style={{ marginLeft: "auto", fontSize: 10 }}>{jobTitle.publicId}</Tag>
          )}
        </div>
      </div>

      <div>
        <LabelMeta>New Supervisor</LabelMeta>
        {loadingSupervisors ? (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <Spin />
          </div>
        ) : supervisors.length === 0 ? (
          <Alert
            type="warning"
            message="No supervisors available"
            description="No supervisors are listed for this target job title yet."
            showIcon
          />
        ) : (
          <Select
            placeholder="Select a supervisor"
            style={{ width: "100%", marginTop: 6 }}
            value={selectedSupervisor}
            onChange={setSelectedSupervisor}
            optionLabelProp="label"
          >
            {supervisors.map((sup) => (
              <Select.Option key={sup.id} value={sup.id} label={sup.fullName}>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <span style={{ fontWeight: 500 }}>{sup.fullName}</span>
                  <span style={{ fontSize: 12, color: "#888" }}>
                    {sup.jobTitleName}
                    {sup.designationName ? ` · ${sup.designationName}` : ""}
                  </span>
                </div>
              </Select.Option>
            ))}
          </Select>
        )}
      </div>
    </Modal>
  );
}
StartTransitionModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  employeeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  careerPathId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  jobTitle: PropTypes.object,
  applicationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

// =====================================================================
// Helpers
// =====================================================================
function SectionHeader({ icon, children }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        fontSize: "0.85rem",
        fontWeight: 700,
        color: theme.accentDeep,
        margin: "0 0 10px 0",
        textTransform: "uppercase",
        letterSpacing: 0.5,
      }}
    >
      <span style={{ fontSize: "1rem" }}>{icon}</span>
      {children}
    </div>
  );
}
SectionHeader.propTypes = { icon: PropTypes.node, children: PropTypes.node };

function findTargetJobTitle(paths, fallbackName) {
  // 1. Backend flag — the proper way per spec.
  for (const p of paths) {
    if (!p?.jobTitles) continue;
    const t = p.jobTitles.find((j) => j.isTargetJobTitle === true);
    if (t) return t;
  }
  // 2. Fallback: match by name against the job posting's title.
  if (fallbackName) {
    const target = String(fallbackName).trim().toLowerCase();
    for (const p of paths) {
      if (!p?.jobTitles) continue;
      const t = p.jobTitles.find(
        (j) => String(j.name || "").trim().toLowerCase() === target
      );
      if (t) return t;
    }
  }
  return null;
}

// Find the career path that contains the target job title (the role the
// applicant applied for). This is the actual target path to display in
// the Target Trajectory section — not just whichever path the API
// happens to label as `targetPath`.
function findTargetPath(paths, fallbackName) {
  for (const p of paths) {
    if (!p?.jobTitles) continue;
    if (p.jobTitles.some((j) => j.isTargetJobTitle === true)) return p;
  }
  if (fallbackName) {
    const target = String(fallbackName).trim().toLowerCase();
    for (const p of paths) {
      if (!p?.jobTitles) continue;
      if (
        p.jobTitles.some(
          (j) => String(j.name || "").trim().toLowerCase() === target
        )
      ) {
        return p;
      }
    }
  }
  return null;
}

function formatExperience(job) {
  if (job == null) return null;
  const min = job.minExperienceRequired;
  const max = job.maxExperienceRequired;
  if (min == null && max == null) return null;
  if (max) return `${min ?? 0}–${max} yrs`;
  return `${min ?? 0}+ yrs`;
}

const badgeStyle = (color) => ({
  position: "absolute",
  top: -10,
  right: 10,
  background: color,
  color: "white",
  borderRadius: 40,
  padding: "2px 10px",
  fontSize: "0.65rem",
  fontWeight: 700,
  letterSpacing: 0.4,
  textTransform: "uppercase",
  display: "flex",
  alignItems: "center",
  gap: 4,
});

export default CareerPathTab;
